import { Outlet, Navigate, useLocation } from "react-router-dom";
import * as cookie from "cookie";
import { checkTokenExpired } from "@utils/common";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { useState, useEffect } from "react";
import { setAccessToken } from "@redux/slices/AuthSlice";
import { notifyUser } from "@redux/slices/CommonSlice";
import API_URLS, { API_BASE_URL } from "@constants/apiUrls";
import PageLoader from "@features/common/pageLoader/PageLoader";

// --- Utility Functions for Clarity ---

/** Clears the access token cookie. */
const clearAccessTokenCookie = () => {
  document.cookie =
    "access_token_expiry=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
};

/** Attempts to refresh the access token via the backend. */
const attemptTokenRefresh = async () => {
  try {
    const response = await fetch(API_BASE_URL + API_URLS.REFRESH_TOKEN, {
      method: "POST",
      credentials: "include",
    });

    if (response.status === 400) {
      return { error400: true };
    }

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.access) {
      return null;
    }
    return { access: data.access };
  } catch (error) {
    return null;
  }
};

const PrivateRoute = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let isMounted = true; // Prevents state updates on unmounted components

    const verify = async () => {
      setIsChecking(true);

      const cookies = cookie.parse(document.cookie);
      const currentExpiry = cookies.access_token_expiry;

      // 1. If we have a token in Redux AND a valid cookie, we are logged in.
      // We check the token value directly from the Redux selector at this moment.
      if (accessToken && currentExpiry && !checkTokenExpired(currentExpiry)) {
        if (isMounted) {
          setIsAuthenticated(true);
          setIsChecking(false);
        }
        return;
      }

      // 2. If Redux is empty OR cookie is expired, try to refresh
      const result = await attemptTokenRefresh();

      if (isMounted) {
        if (result?.access) {
          dispatch(setAccessToken(result.access));
          setIsAuthenticated(true);
        } else {
          clearAccessTokenCookie();
          setIsAuthenticated(false);

          // Only show notification if it's a specific refresh error (400)
          if (result?.error400) {
            dispatch(
              notifyUser({
                title: "Session expired",
                message: "Your session has expired. Please log in again.",
                severity: "error",
              }),
            );
          }
        }
        setIsChecking(false);
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
    // Re-verify if dispatch or accessToken changes
  }, [dispatch, accessToken]);

  if (isChecking) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <PageLoader />
      </div>
    );
  }
  if (!isAuthenticated) {
    // ✅ Build full URL with current path and query params
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;
    const encodedRedirect = encodeURIComponent(currentUrl);

    // Show notifier and redirect
    return (
      <>
        <Navigate to={`/login?redirect=${encodedRedirect}`} replace />
      </>
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
